import { createUseStyles } from 'react-jss';
import { theme } from '@align-cpf/uikit-styles';

const { colors, spacing } = theme;

const focusedWithWhiteBorder = {
  '&:after': {
    content: '""',
    position: 'absolute',
    top: 2,
    left: 2,
    bottom: 2,
    right: 2,
    borderRadius: 2,
    boxShadow: [0, 0, 0, 1, colors.borderOnColorFocus],
    opacity: 0,
    transition: 'opacity .2s ease-out',
  },
  '&:focus-visible': {
    '&:after': {
      opacity: 1,
    },
  },
};

const focusedWithBorder = {
  '&:focus-visible': {
    boxShadow: [0, 0, 0, 1, colors.borderFocus, 'inset'],
  },
};

const disabledWithBackground = {
  '$disabled&': {
    pointerEvents: 'none',
    backgroundColor: colors.backgroundInteractiveDisabled,
  },
};

const disabledWithoutBackground = {
  '$disabled&': {
    pointerEvents: 'none',
    backgroundColor: 'transparent',
  },
};

const ghostish = {
  '&:hover, $hover&': { backgroundColor: colors.backgroundSubtleHover },
  '&:active, $active&': { backgroundColor: colors.backgroundSubtleActive },
  ...disabledWithoutBackground,
  ...focusedWithBorder,
};

export default createUseStyles(
  {
    root: {
      userSelect: 'none',
      background: 'none',
      border: 'none',
      position: 'relative',
      letterSpacing: 0,
      borderRadius: spacing.sp01,
      padding: 0,
      margin: 0,
      fontFamily: 'inherit',
      display: 'inline-block',
      transition: 'all .25s ease-out',
      height: 'max-content',
      boxShadow: [0, 0, 0, 1, 'transparent'],
      outline: 'none',
      overflow: 'hidden',
      cursor: 'pointer',
    },

    loading: {
      pointerEvents: 'none',
    },

    fullWidth: {
      width: '100%',
    },

    iconOnly: {},

    content: {
      '& > :not(:empty)': {
        marginLeft: spacing.sp02,
      },
    },

    internal: {
      display: 'flex',
      flexShrink: 0,
      flexGrow: 1,
      justifyContent: 'center',

      '$medium &': {
        padding: [spacing.sp03, spacing.sp04],
      },

      '$small &': {
        padding: [spacing.sp02, spacing.sp04],
      },

      '$medium$iconOnly &': {
        padding: spacing.sp03,
      },

      '$small$iconOnly &': {
        padding: spacing.sp02,
      },
    },

    hover: {},
    active: {},
    focus: {},

    icon: {
      height: 20,
      width: 20,
      minHeight: 20,
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
    },

    /* SIZES */

    small: {},
    medium: {},

    disabled: {
      pointerEvents: 'none',
      '& $internal': {
        color: colors.textDisabled,
      },
    },

    /* VARIANTS */
    primary: {
      color: colors.textOnColorPrimary,
      backgroundColor: colors.backgroundInteractive,
      outlineOffset: -2,

      '&:hover, $hover&': {
        backgroundColor: colors.backgroundInteractiveHover,
      },

      '&:active, $active&': {
        backgroundColor: colors.backgroundInteractiveActive,
      },

      ...focusedWithWhiteBorder,
      ...disabledWithBackground,
    },

    secondary: {
      color: colors.textPrimary,
      boxShadow: [0, 0, 0, 1, colors.borderAccent, 'inset'],

      '&:active, $active&': {
        boxShadow: [0, 0, 0, 1, colors.borderAccentActive, 'inset'],
      },

      '&:hover, $hover&': {
        boxShadow: [0, 0, 0, 1, colors.borderAccentHover, 'inset'],
      },

      '$disabled&': {
        opacity: 1,
        boxShadow: [0, 0, 0, 1, colors.borderDisabled, 'inset'],
      },
      '&:focus-visible, $focus': {
        boxShadow: [0, 0, 0, 1, colors.borderFocus, 'inset'],
      },
    },

    ghost: {
      color: colors.textPrimary,
      ...ghostish,
    },

    link: {
      color: colors.textLink,
      ...ghostish,
    },

    danger: {
      color: colors.textOnColorPrimary,
      backgroundColor: colors.backgroundDestructive,
      outlineOffset: -2,

      '&:hover, $hover&': {
        backgroundColor: colors.backgroundDestructiveHover,
      },

      '&:active, $active&': {
        backgroundColor: colors.backgroundDestructiveActive,
      },

      ...disabledWithBackground,
      ...focusedWithWhiteBorder,
    },
  },
  {
    name: 'Button',
  },
);
